<template>
  <div
    class="v3-custom-select ps__child--consume"
    :class="{
      'is-invalid': invalidFeedback || isInvalid
    }"
  >
    <VSelect
      :model-value="modelValue"
      v-bind="{
        selected,
        multiple,
        options,
        clearable,
        placeholder,
        autoscroll,
        closeOnSelect,
        filterable,
        reduce,
        selectable,
        searchable,
        title,
        disabled,
        deselectFromDropdown,
        clearCacheOnBlur
      }"
      @search="onSearch"
      @update:model-value="onChange"
      @open="onOpenSelect"
      @close="onCloseSelect"
    >
      <template v-if="customTemplate" #option="option">
        <slot name="custom-template" :option="option"></slot>
      </template>
      <template v-if="selectOnlyNames" #selected-option="{ label }">
        <span class="selected-option-label">{{ splitText(label) }}</span>
      </template>
      <slot name="list-footer"></slot>
      <slot name="footer"></slot>
      <template #no-options>
        <slot name="no-options">
          <span class="text-black-50 pm-1 d-block">
            {{ $t("common.sorry_no_matching_options") }}
          </span>
        </slot>
      </template>
      <template #open-indicator="{ attributes }">
        <span class="options-length-label" v-if="selectedOptions > 3"
          >+{{ selectedOptions - 3 }} {{ $t("common.more") }}</span
        >
        <i :class="appendIcon" v-bind="attributes"></i>
      </template>
    </VSelect>
    <slot name="content"></slot>
    <div v-if="invalidFeedbackMessage" class="invalid-message text-start">
      {{ invalidFeedbackMessage }}
    </div>
  </div>
</template>

<script>
import { computed } from "vue"

const clampValue = (value, maxLength) => {
  const isString = typeof value === "string"
  if (isString) {
    return value?.length > maxLength ? value.slice(0, maxLength) : value
  }
  return value
}

export default {
  // For adding more props or functionalities for v-select check its own: https://vue-select.org/api/props.html
  props: {
    selected: Object,
    multiple: Boolean,
    options: Array,
    clearable: Boolean,
    placeholder: String,
    autoscroll: Boolean,
    closeOnSelect: Boolean,
    filterable: Boolean,
    reduce: Function,
    selectable: Function,
    searchable: { type: Boolean, default: true },
    title: String,
    disabled: Boolean,
    deselectFromDropdown: Boolean,
    modelValue: [String, Object, Array],
    invalidFeedback: [String, Array],
    isInvalid: Boolean,
    readonly: { type: Boolean, default: false },
    appendIcon: {
      type: String,
      default: "ri-arrow-down-s-line"
    },
    clearCacheOnBlur: Function,
    selectOnlyNames: {
      type: Boolean,
      default: false
    },
    customTemplate: {
      type: Boolean,
      default: false
    },
    maxLength: {
      type: Number,
      default: 500
    }
  },
  emits: ["search", "update:modelValue", "open", "close"],
  setup(props, { emit }) {
    const onSearch = (value) => {
      const slicedValue = clampValue(value, props.maxLength)
      emit("search", slicedValue)
    }
    const onChange = (value) => {
      if (props.readonly) return
      const slicedValue = clampValue(value, props.maxLength)
      emit("update:modelValue", slicedValue)
    }

    const onOpenSelect = (value) => {
      const slicedValue = clampValue(value, props.maxLength)
      emit("open", slicedValue)
    }

    const onCloseSelect = (value) => {
      const slicedValue = clampValue(value, props.maxLength)
      emit("close", slicedValue)
    }

    const selectedOptions = computed(() => {
      return props.multiple &&
        props.modelValue &&
        Array.isArray(props.modelValue)
        ? props.modelValue.length
        : ""
    })

    const invalidFeedbackMessage = computed(() => {
      return props.invalidFeedback && Array.isArray(props.invalidFeedback)
        ? props.invalidFeedback[0]
        : props.invalidFeedback
    })

    const splitText = (text) => {
      const textArray = text.split(",")
      return textArray[0]
    }

    return {
      onSearch,
      onChange,
      onOpenSelect,
      onCloseSelect,
      selectedOptions,
      invalidFeedbackMessage,
      splitText
    }
  }
}
</script>

<style></style>
